import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Box,
    UserCircle,
    Building2,
    Calendar,
    Signature,
    CheckCircle2,
    Image as ImageIcon,
    Clock,
    User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { AssetAssignment } from '../../types/asset';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent } from '../../components/ui/dialog';

const AssignmentDetails: React.FC = () => {
    const { id, assignmentId } = useParams<{ id: string, assignmentId: string }>();
    const navigate = useNavigate();
    const [assignment, setAssignment] = useState<AssetAssignment | null>(null);
    const [loading, setLoading] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');

    useEffect(() => {
        const fetchAssignmentDetails = async () => {
            try {
                setLoading(true);
                const res = await api.get<AssetAssignment>(`/api/assets/${id}/assignments/${assignmentId}`);
                setAssignment(res);
            } catch (error) {
                console.error('Error fetching assignment details:', error);
                toast.error('Failed to load assignment details');
            } finally {
                setLoading(false);
            }
        };
        if (id && assignmentId) {
            fetchAssignmentDetails();
        }
    }, [id, assignmentId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!assignment) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Assignment not found</h2>
                <Button onClick={() => navigate(`/assets/${id}`)} className="mt-4">Back to Asset</Button>
            </div>
        );
    }

    const isReturned = !!assignment.returnedDate;

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50"
                    onClick={() => navigate(`/assets/${id}`)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        Assignment Details
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Comprehensive view of the assignment record #{(assignment.id as any).toString().padStart(4, '0')}
                    </p>
                </div>
            </div>

            <div className="space-y-6">

                {/* 1. Global Assignment Information */}
                <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Box size={24} className="text-blue-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Initial Handover</h2>
                                <p className="text-gray-500 text-xs font-medium">Original assignment details</p>
                            </div>
                        </div>
                        <Badge className={`${isReturned ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'} border-0 font-bold uppercase tracking-widest text-[10px]`}>
                            {isReturned ? 'Completed' : 'Assigned'}
                        </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Date Assigned</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <Calendar size={14} className="text-blue-500" />
                                {toSydneyDate(assignment.assignedDate)}
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Assigned By</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <User size={14} className="text-blue-500" />
                                {assignment.assignedBy?.name || 'System Admin'}
                            </div>
                        </div>
                        <div className="lg:col-span-2">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Assigned To</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                {assignment.assignedToUser ? <UserCircle size={16} className="text-blue-600" /> : <Building2 size={16} className="text-orange-500" />}
                                {assignment.assignedToUser?.name || assignment.assignedToDepartment?.departmentName || 'Unknown'}
                            </div>
                        </div>
                    </div>

                    {(assignment.notes || assignment.signatureData) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                            {assignment.notes && (
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Assignment Notes</span>
                                    <p className="text-sm font-medium text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">
                                        {assignment.notes}
                                    </p>
                                </div>
                            )}
                            {assignment.signatureData && (
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Recipient Signature</span>
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-[100px] flex items-center justify-center">
                                        {assignment.signatureType === 'Type' ? (
                                            <span className="font-[signature] text-3xl text-gray-800">{assignment.signatureData}</span>
                                        ) : (
                                            <img src={assignment.signatureData} alt="Recipient Signature" className="max-h-full max-w-full mix-blend-multiply" />
                                        )}
                                    </div>
                                    <p className="text-[10px] font-bold text-gray-400 text-center uppercase">Signed during assignment</p>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* 2. Return Information */}
                {isReturned && (
                    <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={24} className="text-emerald-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Return Protocol</h2>
                                <p className="text-gray-500 text-xs font-medium">Logged condition and custody transfer</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-emerald-50 p-6 rounded-2xl border border-emerald-100">
                            <div>
                                <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest block mb-1">Date Returned</span>
                                <div className="flex items-center gap-2 font-bold text-emerald-900">
                                    <Calendar size={14} className="text-emerald-600" />
                                    {toSydneyDate(assignment.returnedDate!)}
                                </div>
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest block mb-1">Returned By (Assignee)</span>
                                <div className="flex items-center gap-2 font-bold text-emerald-900">
                                    <User size={14} className="text-emerald-600" />
                                    {assignment.returnedBy?.name || 'Unknown User'}
                                </div>
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest block mb-1">Accepted By (Receiver)</span>
                                <div className="flex items-center gap-2 font-bold text-emerald-900">
                                    <User size={14} className="text-emerald-600" />
                                    {assignment.returnedTo?.name || 'System Admin'}
                                </div>
                            </div>
                        </div>

                        {/* Dual Signatures */}
                        {(assignment.returnSignatureData || assignment.receiverSignatureData) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Assignee Return Signature</span>
                                    {assignment.returnSignatureData ? (
                                        <>
                                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 h-[100px] flex items-center justify-center">
                                                {assignment.returnSignatureType === 'Type' ? (
                                                    <span className="font-[signature] text-3xl text-gray-800">{assignment.returnSignatureData}</span>
                                                ) : (
                                                    <img src={assignment.returnSignatureData} alt="Return Signature" className="max-h-full max-w-full mix-blend-multiply" />
                                                )}
                                            </div>
                                            <p className="text-[10px] font-bold text-gray-400 text-center uppercase">Asset Handed Over By</p>
                                        </>
                                    ) : (
                                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 border-dashed h-[100px] flex items-center justify-center">
                                            <span className="text-xs font-bold text-gray-400">Signature bypassed or not provided</span>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest block">Receiving Person Signature</span>
                                    {assignment.receiverSignatureData ? (
                                        <>
                                            <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 h-[100px] flex items-center justify-center">
                                                {assignment.receiverSignatureType === 'Type' ? (
                                                    <span className="font-[signature] text-3xl text-emerald-900">{assignment.receiverSignatureData}</span>
                                                ) : (
                                                    <img src={assignment.receiverSignatureData} alt="Receiver Signature" className="max-h-full max-w-full mix-blend-multiply" />
                                                )}
                                            </div>
                                            <p className="text-[10px] font-bold text-emerald-600/70 text-center uppercase">Asset Verified & Accepted By</p>
                                        </>
                                    ) : (
                                        <div className="bg-red-50 p-4 rounded-xl border border-red-100 border-dashed h-[100px] flex items-center justify-center">
                                            <span className="text-xs font-bold text-red-500">Missing receiver signature</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Return Photos */}
                        {((assignment as any).returnPhotosUrl && (assignment as any).returnPhotosUrl.length > 0) && (
                            <div className="space-y-4 pt-4 border-t border-gray-100">
                                <div className="flex items-center gap-2">
                                    <ImageIcon size={18} className="text-indigo-500" />
                                    <h3 className="text-sm font-bold text-gray-900">Return Photos</h3>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {(assignment as any).returnPhotosUrl.map((photoUrl: string, idx: number) => (
                                        <div
                                            key={idx}
                                            className="aspect-square rounded-2xl overflow-hidden border border-gray-200 cursor-pointer group relative"
                                            onClick={() => {
                                                setSelectedImage(photoUrl);
                                                setIsImageModalOpen(true);
                                            }}
                                        >
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10" />
                                            <img src={photoUrl} alt={`Return documentation ${idx + 1}`} className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                )}
            </div>

            {/* Image Modal */}
            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-transparent border-none shadow-none">
                    <img
                        src={selectedImage}
                        alt="Return Documentation Full View"
                        className="w-full h-auto max-h-[90vh] object-contain rounded-2xl ring-1 ring-white/10 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AssignmentDetails;
